import { useRef, useState } from "react";
import useGetSpectrum from "./hooks/useGetSpectrum";
import SpectrumPreview from "./SpectrumPreview";
import { P2 } from "../../components/Paragraph";
import styles from "./spectrumCardHoverable.module.css";
import useLang from "../../hooks/useLang";
import useSimpleDate from "../../hooks/useSimpleDate";
import getProbabilityTag from "../../utils/getProbabilityTag";
import classNames from "classnames";
import getGroupActions from "./utils/getGroupActions";
import getConfusionActions from "./utils/getConfusionActions";
import Button from "../../components/Button";
import Modal from "../../components/Modal";
import { usePatchFeatureExampleMutation } from "../features/featuresSlice";

const valueToGroupType = (value) => {
  if (value === 1) return "POSITIVE";
  if (value === 0) return "NEGATIVE";
  return "UNASSIGNED";
};

const SpectrumCardHoverable = ({
  id,
  spectrumData,
  value,
  lastEditAt,
  labelSource,
  featureId,
  probability,
  inferenceState,
  showProbability = false,
}) => {
  const idRef = useRef(crypto.randomUUID());
  const [isEnlarged, setIsEnlarged] = useState(false);
  const lang = useLang();
  const { getSimpleDate } = useSimpleDate();
  const { data: fetchedSpectrum } = useGetSpectrum({
    id: spectrumData ? null : id,
  });
  const [patchFeatureExample] = usePatchFeatureExampleMutation();

  const data = spectrumData ?? fetchedSpectrum ?? null;
  const groupType = valueToGroupType(value);
  const groupActions = getGroupActions(groupType);
  const prognosisActions = getConfusionActions(groupType, probability);

  const dispatchLabel = (action) => {
    if (!featureId || !id || !action) return;
    patchFeatureExample({
      params: { featureId, spectrumId: id },
      body: { label: action.toLowerCase() },
    });
  };

  const handleGroupActionClick = (action) => {
    dispatchLabel(action);
  };

  const handlePrognosisActionClick = (action) => {
    dispatchLabel(action?.id);
  };

  const renderActionButtons = () =>
    showProbability ? (
      <div style={{ display: "flex", gap: "var(--gap-sm)" }}>
        {Array.isArray(prognosisActions)
          ? prognosisActions?.map((action, idx) => (
              <Button
                key={`${idRef?.current}-${idx}`}
                important={action?.type === "RECOMENDED"}
                warning={action?.type === "ALERT"}
                onClick={() => {
                  handlePrognosisActionClick(action);
                }}
              >
                {lang(`captions.${action?.id?.toLowerCase()}`)}
              </Button>
            ))
          : null}
      </div>
    ) : (
      <div style={{ display: "flex", gap: "var(--gap-sm)" }}>
        {Array.isArray(groupActions)
          ? groupActions?.map((action, idx) => (
              <Button
                key={`${idRef?.current}-${idx}`}
                onClick={() => {
                  handleGroupActionClick(action);
                }}
              >
                {lang(`captions.${action?.toLowerCase()}`)}
              </Button>
            ))
          : null}
      </div>
    );

  const displayDate = lastEditAt ?? data?.date_modified;

  const inferenceBadge =
    showProbability && inferenceState && inferenceState !== "completed"
      ? lang(`prognosis.inference_state.${inferenceState}`)
      : null;

  return (
    <div className={styles.spectrumCardHoverable}>
      {showProbability && typeof probability === "number" ? (
        <div
          className={classNames(
            styles.probability,
            styles[getProbabilityTag(probability)]
          )}
        >
          {`${lang("captions.probability")}: ${probability.toFixed(3)}`}
        </div>
      ) : null}
      {inferenceBadge ? (
        <div className={styles.inferenceState}>{inferenceBadge}</div>
      ) : null}
      <SpectrumPreview data={data} viewOnly size={"small"} />
      <div>
        <P2>{`${lang("captions.date_modified")} ${getSimpleDate(
          displayDate
        )}`}</P2>
        {labelSource === "report" ? (
          <span className={styles.reportMarker}>
            {lang("captions.from_report")}
          </span>
        ) : null}
      </div>
      <div className={styles.hoverContainer}>
        {showProbability && typeof probability === "number" ? (
          <>
            <div
              className={classNames(
                styles.probability,
                styles[getProbabilityTag(probability)]
              )}
            >
              {`${lang("captions.probability")}: ${probability.toFixed(3)}`}
            </div>
          </>
        ) : null}
        <div className={styles.spacer} />
        {renderActionButtons()}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "var(--gap-xs)",
            marginTop: "10px",
          }}
        >
          {data ? (
            <Button
              inline
              unpadded
              navigation
              icon="fitToScreen"
              onClick={() => setIsEnlarged(true)}
            >
              {lang("spectra.enlarge")}
            </Button>
          ) : null}
          {id ? (
            <Button
              inline
              unpadded
              navigation
              icon="example"
              to={`/spectra/${id}`}
              target="_blank"
            >
              {lang("spectra.open_example")}
            </Button>
          ) : null}
        </div>
        <div className={styles.spacer} />
      </div>
      <Modal isOpen={isEnlarged} onClose={() => setIsEnlarged(false)}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--gap)",
          }}
        >
          {showProbability && typeof probability === "number" ? (
            <div
              className={classNames(
                styles.probability,
                styles[getProbabilityTag(probability)]
              )}
            >
              {`${lang("captions.probability")}: ${probability.toFixed(3)}`}
            </div>
          ) : null}
          <SpectrumPreview data={data} viewOnly />
          <P2>{`${lang("captions.date_modified")} ${getSimpleDate(
            displayDate
          )}`}</P2>
          <div style={{ display: "flex", justifyContent: "center" }}>
            {renderActionButtons()}
          </div>
          <div style={{ display: "flex", gap: "var(--gap-sm)" }}>
            {id ? (
              <Button
                inline
                unpadded
                navigation
                icon="example"
                to={`/spectra/${id}`}
                target="_blank"
              >
                {lang("spectra.open_example")}
              </Button>
            ) : null}
            <div style={{ marginLeft: "auto" }}>
              <Button onClick={() => setIsEnlarged(false)}>
                {lang("common.close")}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SpectrumCardHoverable;
